
import { buildPreviewDocument } from "./PreviewDocument";

export interface PreviewManagerOptions {
    getConsoleScript: () => string;
    assetBaseURL?: string | null;
}

const SANDBOX_FLAGS = "allow-scripts allow-forms allow-modals allow-popups";
const NEW_TAB_POLL_INTERVAL_MS = 1000;

function buildNewTabWrapper(userDocUrl: string): string {
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Preview</title>
<style>
html, body { margin: 0; padding: 0; height: 100%; background: #ffffff; }
iframe { display: block; width: 100%; height: 100%; border: 0; }
</style>
</head>
<body>
<iframe sandbox="${SANDBOX_FLAGS}" src="${userDocUrl}"></iframe>
</body>
</html>`;
}

export class PreviewManager {
    private previewFrame: HTMLIFrameElement | null = null;
    private previewDevicesContainer: HTMLElement | null = null;

    private _currentPreviewUrl: string | null = null;
    private _destroyed: boolean = false;
    private options: PreviewManagerOptions;

    constructor(options: PreviewManagerOptions) {
        this.options = options;
    }

    setElements(frame: HTMLIFrameElement, devicesContainer: HTMLElement) {
        this.previewFrame = frame;
        this.previewDevicesContainer = devicesContainer;
    }

    private resolveAssetBase(): string | null {
        if (this.options.assetBaseURL) {
            return this.options.assetBaseURL;
        }
        try {
            return document.baseURI;
        } catch (e) {
            return null;
        }
    }

    update(userCode: string) {
        if (!this.previewFrame || this._destroyed) return;

        // Cleanup previous URL
        if (this._currentPreviewUrl) {
            URL.revokeObjectURL(this._currentPreviewUrl);
            this._currentPreviewUrl = null;
        }

        const fullCode = buildPreviewDocument(userCode, {
            baseUrl: this.resolveAssetBase(),
            instrumentScript: this.options.getConsoleScript()
        });

        const blob = new Blob([fullCode], { type: "text/html;charset=utf-8" });
        this._currentPreviewUrl = URL.createObjectURL(blob);
        this.previewFrame.src = this._currentPreviewUrl;
    }

    openNewTab(userCode: string) {
        if (this._destroyed) return;

        // The user document itself is generated through the same pipeline as the
        // inline preview (doctype preserved, base tag consistent across routes).
        // No console bridge here: the top-level wrapper is not a console host.
        const userDoc = buildPreviewDocument(userCode, {
            baseUrl: this.resolveAssetBase()
        });
        const userBlob = new Blob([userDoc], { type: "text/html;charset=utf-8" });
        const userUrl = URL.createObjectURL(userBlob);

        // The new tab only ever shows a trusted wrapper; the user's code runs
        // inside a sandboxed iframe without allow-same-origin, so it cannot
        // reach the wrapper document or the embedding application's origin.
        const wrapperBlob = new Blob([buildNewTabWrapper(userUrl)], { type: "text/html;charset=utf-8" });
        const wrapperUrl = URL.createObjectURL(wrapperBlob);

        const newTab = window.open(wrapperUrl, "_blank");
        if (newTab) {
            // Defense in depth: sever the opener relationship explicitly.
            try { newTab.opener = null; } catch (e) { /* cross-origin: already isolated */ }
            const cleanupInterval = window.setInterval(() => {
                if (!newTab.closed) return;
                window.clearInterval(cleanupInterval);
                URL.revokeObjectURL(wrapperUrl);
                URL.revokeObjectURL(userUrl);
            }, NEW_TAB_POLL_INTERVAL_MS);
        } else {
            URL.revokeObjectURL(wrapperUrl);
            URL.revokeObjectURL(userUrl);
            alert("Popup blocked! Please allow popups for this site to open the preview in a new tab.");
        }
    }

    resizeDevice(width: string, height: string) {
        if (this.previewDevicesContainer) {
            this.previewDevicesContainer.style.maxWidth = width;
            this.previewDevicesContainer.style.height = height;
            this.previewDevicesContainer.style.margin = width !== "100%" ? "auto" : "";
        }
    }

    destroy() {
        this._destroyed = true;
        if (this._currentPreviewUrl) {
            URL.revokeObjectURL(this._currentPreviewUrl);
            this._currentPreviewUrl = null;
        }
        this.previewFrame = null;
        this.previewDevicesContainer = null;
    }
}
